import { Box, Button, Checkbox, List, Table, TableBody, TableCell, TableCellProps, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Fragment, useMemo, useState } from "react";
import { IItensPorLojaDetalhes } from "../../CardItens";
import DialogGerarReceituario from "./DialogGerarReceituario";
import { usePermission } from "../../../../../../../components/PermissionGuard";
import { TProdutosDetalhes } from "../../../../../../../types/api/comercial/detalhar-pedido";
import SituacaoExpedicaoChip from "../../../../../../../components/SituacaoExpedicao";
import truncarString from "../../../../../../../helpers/truncarString";
import { enqueueSnackbar } from "notistack";
import DialogExpedirItens from "./DialogExpedirItens";
import { CardProps } from "../..";
import AlertReceituarioAgronomico from "./AlertReceituarioAgronomico";

export default function StepExpedicao({ data }: StepExpedicaoProps) {
    const hasPermission = usePermission("comercial.realizar-expedicao");
    const [openGerarReceituario, setOpenGerarReceituario] = useState(false);
    const [openExpedirItens, setOpenExpedirItens] = useState<TProdutosDetalhes[] | null>(null);
    const [selected, setSelected] = useState<TProdutosDetalhes[]>([]);

    const receituariosEmitido = useMemo(() => {
        return data.itens.some(i => i.receita_id && i.emite_receita === "S")
    }, [data]);

    const itensPorLoja = data.itens.reduce<Record<number, IItensPorLojaDetalhes>>((acc, item) => {
        const lojaId = item.loja.id;
        if (!acc[lojaId]) {
            acc[lojaId] = {
                loja: item.loja,
                itens: []
            };
        }
        acc[lojaId].itens.push(item);
        acc[lojaId].itens.sort((a, b) => a.nome.localeCompare(b.nome));
        return acc;
    }, {});

    // Converte o objeto em array para facilitar o mapeamento no JSX
    const itensPerloja = Object.values(itensPorLoja);

    //Funções manipuladoras
    const handleChangeCheckbox = (row: TProdutosDetalhes) => {
        const index = selected.findIndex(i => i.id === row.id)
        if (index >= 0) {
            setSelected(prev => prev.filter(i => i.id !== row.id))
        } else {
            setSelected(prev => [...prev, row])
        }
    }

    const handleChangeAllCheckbox = (lojaId: number) => {
        const itens = itensPorLoja[lojaId].itens.filter(i => i.quantidade_entregue < i.quantidade)
        const allSelected = itens.every(i => selected.some(s => s.id === i.id))

        if (itens.every(i => i.quantidade_entregue === i.quantidade)) {
            return enqueueSnackbar("Todos os itens dessa loja já foram expedidos", { variant: "warning" })
        }

        if (allSelected) {
            setSelected(prev => prev.filter(i => !itens.some(s => s.id === i.id)))
        } else {
            const itensNaoSelecionados = itens.filter(item => !selected.some(s => s.id === item.id))
            setSelected(prev => [...prev, ...itensNaoSelecionados])
        }
    }
    //

    return (
        <Box mt={2}>
            {
                receituariosEmitido &&
                <AlertReceituarioAgronomico data={data} />
            }

            <List sx={{ mt: 2 }}>
                {itensPerloja.map((item, index) => {
                    const allSelected = item.itens.every(i => selected.some(s => s.id === i.id))
                    return (
                        <Fragment key={index}>
                            <Typography
                                mb={1}
                                variant="subtitle2"
                            >
                                <b>{item.loja.id} - {item.loja.nome}</b>
                            </Typography>
                            <TableContainer sx={{ mb: 2 }} >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCellSemBorder padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={allSelected}
                                                    disabled={itensPorLoja[item.loja.id].itens.every(i => i.quantidade_entregue === i.quantidade) || !hasPermission}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleChangeAllCheckbox(item.loja.id)
                                                    }}
                                                />
                                            </TableCellSemBorder>
                                            <TableCellSemBorder><b>Produto</b></TableCellSemBorder>
                                            <TableCellSemBorder align='right'><b>Quantidade</b></TableCellSemBorder>
                                            <TableCellSemBorder align='right'><b>Expedido</b></TableCellSemBorder>
                                            <TableCellSemBorder align="right"></TableCellSemBorder>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {
                                            item.itens.map((item, index) => {
                                                const checked = selected.some(i => i.id === item.id)
                                                let status = "Aguardando expedição"
                                                if (item.quantidade <= item.quantidade_entregue) status = "Concluido"

                                                return (
                                                    <TableRow key={index}>
                                                        <TableCellSemBorder padding="checkbox">
                                                            <Checkbox
                                                                color="primary"
                                                                checked={checked}
                                                                disabled={item.quantidade_entregue === item.quantidade || status === "Receituário obrigatório" || !hasPermission}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleChangeCheckbox(item)
                                                                }}
                                                            />
                                                        </TableCellSemBorder>
                                                        <TableCellSemBorder>{truncarString(item.nome, 24)}</TableCellSemBorder>
                                                        <TableCellSemBorder align='right'>{item.quantidade}</TableCellSemBorder>
                                                        <TableCellSemBorder align='right'>{item.quantidade_entregue}</TableCellSemBorder>
                                                        <TableCellSemBorder align="right"><SituacaoExpedicaoChip situacao={status} /></TableCellSemBorder>
                                                    </TableRow>
                                                )
                                            })
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Fragment>
                    )
                })}
            </List>
            {hasPermission && (
                <Button
                    variant="contained"
                    onClick={() => setOpenExpedirItens(selected)}
                    disabled={selected.length === 0 || selected.some(i => i.emite_receita === "S" && !i.receita_emitida)}
                >
                    Expedir itens ({selected.length})
                </Button>
            )}

            {
                Boolean(openExpedirItens) && openExpedirItens &&
                <DialogExpedirItens
                    idPedido={data.id}
                    open={Boolean(openExpedirItens)}
                    data={openExpedirItens}
                    handleClose={() => {
                        setOpenExpedirItens(null)
                        setSelected([])
                    }}
                />
            }
            {
                openGerarReceituario &&
                <DialogGerarReceituario
                    {...{ data }}
                    open={openGerarReceituario}
                    handleClose={() => setOpenGerarReceituario(false)}
                />
            }

        </Box>
    )
}

export const TableCellSemBorder = (props: TableCellProps) => {
    return <TableCell {...props} sx={{ borderBottom: "none", ...props.sx }} />;
};

interface StepExpedicaoProps extends CardProps { }
